import { $t } from '@/i18n';
import { omit } from 'lodash';
import { message } from '@utils/wuji-message';
import qs from 'query-string';

import wujiFetch from '@utils/wujiFetch';
import { addPathPrefix } from '@/utils/path';
import { getDesignEnvPath } from '@config/constant';
import { convertDescriptionToDiscardPayload } from '@/components/version/utils/diff-tree';
import { publishFetch } from '@/modules/ai-page/publish-fetch';
const getPathPrefix = env => `${getDesignEnvPath(env)}/version`;
const PAGE_SIZE = 10;

export const VERSION_STATUS = {
  UNRELEASED: 0, // 未发布or下线了
  RELEASED: 1, // 已发布
  IN_GRAY: 2, // 灰度
  PRE_RLEASED: 3, // 预发布（只有正式环境有这个状态）
  REJECT: 4, // 打回（只有正式环境有），在预发布状态选择了取消发布
};

export const VERSION_ID_QUERY_KEY = 'versionId';
export const VERSION_ENV_QUERY_KEY = 'currentEnv';
export const VERSION_SEARCH_QUERY_KEY = 'versionSearch';

const getSearch = search => (search ? `&search=${search}` : '');

const getReleasedVersion = async (env, projectId) => await wujiFetch(`${getPathPrefix(env)}/project/release?projectid=${projectId}`);
export const fetchLatestSemver = async ({ projectId, env }) => {
  const url = `${getPathPrefix(env)}/project/semver/latest`;
  const result = await wujiFetch(
    `${url}?projectid=${projectId}`,
    { method: 'GET', timeout: 30 * 1000 },
  );
  return result?.[0]?.semver ?? '';
};
export const fetchVersionList = async ({ projectId, env, page = 1, size = PAGE_SIZE, search }) => {
  const url = `${getPathPrefix(env)}/project`;
  const result = await wujiFetch(
    `${url}?projectid=${projectId}&page=${page}&size=${size}&sort=_ctime&order=desc${getSearch(search)}`,
    { method: 'GET', timeout: 30 * 1000 },
  );
  return result;
};
const timeout = 60000 * 5; // 5分钟

export default {
  state: {
    versionList: {},
    currentVersion: {},
    fromReleasedVersion: {},
    envPageList: [],
    total: 0, // 总数
    page: 1, // 当前页
    search: undefined, // 搜索条件
    fieldsMap: {}, // 发布版本字段名映射
  },
  getters: {
  },
  actions: {
    /**
     * 获取两个环境 / 自定义分支与环境的diff
     */
    async fetchDiffContent({ commit, rootState }, { projectId, fromEnv, fromBranch, toEnv }) {
      let diffList = [];
      const proId = projectId || rootState.project.projectInfo?.id;
      try {
        if (fromEnv === 'dev') {
          if (fromBranch && fromBranch !== 'master') {
            // 分支发布
            const url = `${getPathPrefix(toEnv)}/project/branch/${fromBranch}/staged`;
            diffList = await wujiFetch(`${url}?projectid=${proId}`, { method: 'GET', timeout });
          } else {
            // 开发环境发布
            const url = `${getPathPrefix(toEnv)}/project/staged`;
            diffList = await wujiFetch(`${url}?projectid=${proId}`, { method: 'GET', timeout });
          }
        } else {
          const fromReleasedVersion = await getReleasedVersion(fromEnv, proId);
          commit('updateVersionState', { fromReleasedVersion: fromReleasedVersion || {} });
          if (fromReleasedVersion?.id) {
            const currentVersionId = fromReleasedVersion.id;
            const targetVersion = await wujiFetch(`${getPathPrefix(toEnv)}/project/release?projectid=${proId}`, { timeout });
            diffList = await wujiFetch(`${getPathPrefix(fromEnv)}/project/compare?projectid=${proId}&fromVersionId=${currentVersionId}&toVersionId=${targetVersion?.id || ''}`, {
              timeout,
            });
          } else {
            message.warning($t('k_d65783ce', { arg_0: fromEnv }));
          }
        }

        return diffList;
      } catch (err) {
        message.error($t('k_7d0bf4f5', { arg_0: err.message }));
      }
    },

    async fetchFieldsMap({ rootState, commit }, { projectId }) {
      const pId = projectId || rootState.project.projectInfo?.id;
      // fieldsMap 为全局 schema 元数据，与 env 无关；固定走 dev 路由前缀
      const url = `${getPathPrefix('dev')}/project/fieldsMap?projectid=${pId}`;
      const fieldsMap = await wujiFetch(url, { method: 'GET', timeout });
      commit('updateVersionState', { fieldsMap });
    },


    /**
     *  提交应用版本（只创建不发布）
     */
    async createProjectVersion({ dispatch, rootState }, { projectId, env, branch, semver, desc, data, all, detailed }) {
      const proId = projectId || rootState.project.projectInfo?.id;
      const query = { projectid: proId };
      // 精简数据：一般发布只用到 id 和 schemaKey, schemaId 和 type 用于判断是否为新项目发布
      const getFixData = () => (Array.isArray(data) ? data.map(item => omit(item, ['compareData', 'originData'])) : data);
      if (branch !== 'master') query.branch = branch;
      const result = await wujiFetch(`${getPathPrefix(env)}/project?${qs.stringify(query)}`, {
        method: 'POST',
        body: JSON.stringify({
          semver,
          desc,
          data: detailed ? data : getFixData(),
          all,
          detailed,
        }),
        timeout,
      });

      if (result && result.id) {
        // 透传 release 接口的响应体（含 platform123Pending / recordId 等服务端驱动信息）给调用方
        return await dispatch('publishProjectVersion', { projectId: proId, env, versionId: result.id });
      }
      throw new Error($t('k_332aaa52', { arg_0: result }));
    },

    /**
     *  获取应用版本列表
     */
    async fetchProjectVersionList({ commit, state }, { projectId, env, page = 1, search }) {
      try {
        const url = `${getPathPrefix(env)}/project`;
        const getTotal = wujiFetch(
          `${url}?projectid=${projectId}&count=1${getSearch(search)}`,
          { method: 'GET', timeout: 30 * 1000 },
        );
        const searchChanged = state.search !== search;
        const expectedPage = searchChanged ? 1 : page;
        const getList = fetchVersionList({ projectId, env, page: expectedPage, search });
        const [result, { total }] = await Promise.all([getList, getTotal]);
        commit('updateVersionState', { page: expectedPage, total, search });
        commit('updateVersionList', { env, versions: result });
      } catch (err) {
        message.error($t('k_4b6567de', { arg_0: err.message }));
      }
    },

    /**
     * 发布应用版本
     *
     * 第二次 release（针对同一 versionId 在 staging 验证后的"继续发布"）会命中 server release hook
     * 的 staging-confirm 分支，响应里带 platform123ShrinkPending=true：完成 prod 切指针之后，
     * 前端调 scf register-shrink kind='prod-old' 注册 prod 老 set 缩容（按 shrinkDelayMs 延迟生效）。
     */
    async publishProjectVersion({ dispatch }, { projectId, env, versionId }) {
      try {
        // 返回响应体：服务端可能附带 platform123Pending / platform123ShrinkPending / recordId
        const resp = await wujiFetch(
          `${getPathPrefix(env)}/project/release/${versionId}?projectid=${projectId}`,
          {
            method: 'POST',
            timeout,
          },
        );

        // staging-confirm 路径：server 已完成 prod 切指针，前端需调 scf 注册 prod 老 set 缩容
        if (resp?.platform123ShrinkPending && resp?.responseFlow === 'staging-confirm') {
          try {
            await dispatch('registerPlatform123Shrink', {
              projectId,
              recordId: resp.recordId,
              kind: 'prod-old',
              access: resp.platform123Publish,
            });
          } catch (err) {
            // 缩容注册失败不阻塞主流程（prod 已切指针，缩容是清理动作），仅警告
            console.warn('[platform123] register prod-old shrink failed', err);
          }
        }

        // platform123 prebuild-on-test：dev→test release 完成后 fire-and-forget 触发 prod 镜像预构建。
        //
        // 链路：
        //   1. 调 server `/project/preparePlatform123Prebuild` →
        //      server 同步阻塞 await callUploadToCos 拿 cosPath，签 prebuild token，返 prebuildAccess
        //   2. 拿到 prebuildAccess 后调 publish 服务 `/api/prebuild` 启动 IBE 构建
        //
        // 仅 envId='test' 且 release 不是 platform123 hook 接管路径（即普通 release）时触发。
        // 全程 fire-and-forget，失败仅 console.warn，不阻塞 release 主响应。详见
        // docs/123/platform123-publish-flow.md §2.6 prebuild-on-test。
        const isHookHandled = !!(resp?.platform123Pending || resp?.platform123ShrinkPending);
        if (env === 'test' && !isHookHandled) {
          // void：不 await，发完就走（fire-and-forget）
          void dispatch('triggerPlatform123PrebuildOnTest', {
            projectId,
            env,
            versionId,
          }).catch((err) => {
            console.warn('[platform123][prebuild-on-test] dispatch failed (non-fatal)', err);
          });
        }

        return resp;
      } catch (e) {
        throw new Error($t('k_3712f46e', { arg_0: e.message }));
      }
    },

    // 获取应用当前版本
    async fetchCurrentProjectVersion({ commit }, { projectId, env }) {
      try {
        const url = `${getPathPrefix(env)}/project/release`;
        const result = await wujiFetch(`${url}?projectid=${projectId}`, { method: 'GET' });
        commit('updateVersionState', { currentVersion: result });
      } catch (err) {
        message.error($t('k_a397ab58', { arg_0: err.message }));
      }
    },

    /**
     * platform123 自动触发流程入口（fresh / staging-fresh / rollback 共用）。
     *
     * 由 createProjectVersion / syncProjectVersion / publishProjectVersion 的响应里
     * 检测到 platform123Pending=true 时调用。
     *
     * responseFlow 取值：
     * - 'fresh'：dev→prod 直发 / test→prod sync 链路（无 needStaging）；scf endpoint=/api/publish
     * - 'staging-fresh'：开了 needStaging 的 fresh / sync 链路；scf endpoint 同样为 /api/publish
     *   （scf 端 deploy workflow 不感知 staging 语义，server 端按 record.stagingStatus 在 finalize 时分流写 staging backendInfo）
     * - 'rollback'：版本回滚；scf endpoint=/api/rollback
     *
     * 三条路径都对齐：server 在 hook 内部已经
     *  - createFreshRecord / createRollbackRecord 拿到 recordId
     *  - signPlatform123PublishToken 签好 publish 服务访问凭证（baseUrl + JWT token）
     * 一并放在 hook response 里返回。
     *
     * 前端拿到 response.platform123Publish.{baseUrl, token} 后 POST 到 publish 服务对应 endpoint
     * （Authorization: Bearer ${token}），publish 端 middleware 校验签名 + aud + projectId。
     */
    async triggerPlatform123Publish({ }, { responseFlow, projectId, response }) {
      if (responseFlow !== 'fresh' && responseFlow !== 'staging-fresh' && responseFlow !== 'rollback') {
        throw new Error(`platform123: unknown responseFlow ${responseFlow}`);
      }

      const recordId = response?.recordId;
      if (!recordId) throw new Error(`platform123 ${responseFlow}: missing recordId in response`);

      const access = response?.platform123Publish;
      if (!access?.baseUrl || !access?.token) {
        throw new Error(`platform123 ${responseFlow}: missing platform123Publish access in response (server-side publish service not configured?)`);
      }

      // staging-fresh 与 fresh 在 publish service 端走同一 deploy workflow（mode='fresh'），endpoint 一致
      const endpoint = responseFlow === 'rollback' ? '/api/rollback' : '/api/publish';
      // publishFetch 走原生 fetch（不经 wujiFetch 的 getDataOrThrow / 注入 wuji 内部 headers / 401 reLogin）；
      // publish 服务返 { ok, recordId } / { error }，没有 code 字段，wujiFetch 会误判
      await publishFetch(`${access.baseUrl.replace(/\/+$/, '')}${endpoint}`, {
        method: 'POST',
        body: { projectId, recordId },
        headers: { Authorization: `Bearer ${access.token}` },
        timeout,
      });
      return { recordId };
    },

    /**
     * platform123 prebuild-on-test：在 dev→test release 完成后 fire-and-forget 触发 prod 镜像预构建。
     *
     * 链路（两步）：
     *   1. 调 server `${getPathPrefix(env)}/project/preparePlatform123Prebuild` →
     *      server 同步阻塞 await callUploadToCos 拿 cosPath（耗时 1-5 分钟，与现状 prod 发布同链路；
     *      复用网关长超时白名单），返 prebuildAccess = { baseUrl, token, deployId, expiresAt, payload }
     *   2. 拿到 prebuildAccess 后调 publish 服务 `/api/prebuild` 启动 IBE 镜像构建
     *
     * 服务级总开关 `xySetting.gogoBackend.config123.prebuildOnTestPublish=true` 且项目启用 platform123
     * 时 server 才会返回 ok=true；否则返 ok=false，前端 console.warn 跳过。
     *
     * 失败语义：完全 fire-and-forget；任何步骤失败仅 console.warn，不弹 message、不抛错给上层。
     * publish 服务收到请求后立即返 200，IBE 构建在后台异步跑（前端不轮询进度）。
     *
     * 后续 prod 发布命中预构建产物 → scf 端 run-deploy 跳过 uploading + building → 显著缩短发布耗时。
     * 未命中（产物失败/过期/不存在）→ prod 发布走原 5 阶段流程，与今日行为一致。
     *
     * 详见 docs/123/platform123-publish-flow.md §2.6 prebuild-on-test。
     */
    async triggerPlatform123PrebuildOnTest({ }, { projectId, env, versionId }) {
      // Step 1: 调 server 接口拿 prebuildAccess（同步阻塞等 scf-bed 拿 cosPath）
      const resp = await wujiFetch(
        `${getPathPrefix(env)}/project/preparePlatform123Prebuild?projectid=${projectId}`,
        {
          method: 'POST',
          body: JSON.stringify({ versionId }),
          // server 端同步 await callUploadToCos 最长 10 min；前端给到 11 min 兜底
          timeout: 11 * 60 * 1000,
        },
      );

      if (!resp?.ok || !resp.prebuildAccess) {
        // 服务级开关关 / 配置缺失 / scf-bed 失败等：server 静默返 ok=false；前端不再调 publish
        console.warn('[platform123][prebuild-on-test] server returned not-ok; skip publish dispatch', resp);
        return;
      }

      const access = resp.prebuildAccess;
      if (!access.baseUrl || !access.token || !access.payload) {
        throw new Error('triggerPlatform123PrebuildOnTest: missing baseUrl/token/payload in prebuildAccess');
      }

      // Step 2: 调 publish 服务 /api/prebuild 启动 IBE 构建（路由立即返 200，后台异步跑）
      // payload = { projectId, sourceImageId, cosPath }
      await publishFetch(`${access.baseUrl.replace(/\/+$/, '')}/api/prebuild`, {
        method: 'POST',
        body: access.payload,
        headers: { Authorization: `Bearer ${access.token}` },
        timeout,
      });
    },

    /**
     * 注册 platform123 缩容任务：
     *
     * - kind='prod-old'：staging-confirm 路径完成 prod 切指针后，注册 prod 老 set 缩容
     *   （由 publishProjectVersion 自动调用，无需组件介入）
     *
     * publish 端 register-shrink handler 收到 recordId 后从 server 拉 record 自取 setName/serverName，
     * 前端无法伪造 setName。失败不阻塞主流程，由调用方决定是否重试。
     */
    async registerPlatform123Shrink({ }, { projectId, recordId, kind, access }) {
      if (!recordId) throw new Error('registerPlatform123Shrink: missing recordId');
      if (!access?.baseUrl || !access?.token) {
        throw new Error(`registerPlatform123Shrink ${kind}: missing platform123Publish access`);
      }
      await publishFetch(`${access.baseUrl.replace(/\/+$/, '')}/api/register-shrink`, {
        method: 'POST',
        body: { projectId, recordId, kind },
        headers: { Authorization: `Bearer ${access.token}` },
        timeout,
      });
    },

    /**
     * 应用环境同步
     */
    async syncProjectVersion({ }, { projectId, versionId, fromEnv, toEnv, data, desc, semver, detailed }) {
      try {
        // 如果没有versionId，默认同步当前版本到其他环境
        let verId = versionId;
        if (!versionId) {
          const releasedVersion = await getReleasedVersion(fromEnv, projectId);
          verId = releasedVersion.id;
        }
        // 精简数据：一般发布只用到 id 和 schemaKey
        const getFixData = () => (Array.isArray(data) ? data.map(item => omit(item, ['compareData', 'originData'])) : data);
        const url = `${getPathPrefix(fromEnv)}/project/sync`;
        return wujiFetch(
          `${url}/${verId}?projectid=${projectId}`,
          {
            method: 'POST',
            body: JSON.stringify({
              from: fromEnv,
              to: toEnv,
              data: detailed ? data : getFixData(),
              detailed,
              desc,
              semver,
            }),
            // 这个接口在大型应用耗时会久些，所以延长下超时时间避免前端报错实际接口请求成功
            timeout,
          },
        );
      } catch (err) {
        const res = err.response ? await err.response.json() : { error: err.message };
        throw new Error($t('k_62099491', { arg_0: res.error }));
      }
    },

    /**
     * 获取已发布的版本提交内容
     * - 默认与上一版本对比，展示该版本自身引入的改动
     * - 显式传 compareEnv 时与该环境已发布版本对比，展示累计改动（含中间版本），供发版审批使用
     */
    async fetchVersionCommitContent({ }, { projectId, env, versionId, compareEnv }) {
      try {
        if (compareEnv) {
          const targetVersion = await getReleasedVersion(compareEnv, projectId);
          return await wujiFetch(
            `${getPathPrefix(env)}/project/compare?projectid=${projectId}&fromVersionId=${versionId}&toVersionId=${targetVersion?.id || ''}`,
            { timeout },
          );
        }
        return await wujiFetch(`${getPathPrefix(env)}/project/diff/${versionId}?projectid=${projectId}`);
      } catch (e) {
        throw new Error($t('k_4d4fb2fe'));
      }
    },

    /**
     * 中断发布正式
     */
    async cancelPublish({ }, { projectId, versionId, env }) {
      try {
        await wujiFetch(`${getPathPrefix(env)}/project/cancel/${versionId}?projectid=${projectId}`, { method: 'POST' });
      } catch (err) {
        throw new Error($t('k_155ae064', { arg_0: err.message }));
      }
    },

    /**
     * 撤销（开发环境的）某条变更
     */
    async discardChange({ }, { projectId, env, versionId, appId, schemaId, objectId, desc, type }) {
      try {
        // 变更先暂存到stash
        await wujiFetch(`${getPathPrefix('dev')}/common/stash?projectid=${projectId}`, {
          method: 'POST',
          body: JSON.stringify({
            appId,
            schemaId,
            objectId,
            desc,
          }),
        });
        // 再去更新开发环境的数据
        await wujiFetch(`${getPathPrefix(env)}/common/apply?projectid=${projectId}`, {
          method: 'POST',
          body: JSON.stringify({
            appId,
            schemaId,
            versionId,
            objectId,
            type,
          }),
        });
        return true;
      } catch (err) {
        console.error(err);
        message.error($t('k_37bbc2f3', { arg_0: err.message }));
        return false;
      }
    },

    /**
     * 批量撤销（开发环境的）某条变更
     */
    async batchDiscardChange({ }, { env, projectId, payloads, detailed, detailedPayloads, batch = false }) {
      try {
        return await wujiFetch(`${getPathPrefix(env)}/common/revert?projectid=${projectId}`, {
          method: 'POST',
          body: JSON.stringify({
            batch,
            detailed,
            ...(detailed
              ? { detailedPayloads: detailedPayloads.map(convertDescriptionToDiscardPayload) }
              : { payloads }) }),
          timeout: Math.max(((detailed ? detailedPayloads?.length : payloads?.length) ?? 0) * 3000, 30 * 1000),
        });
      } catch (err) {
        console.error(err);
        message.error($t('k_37bbc2f3', { arg_0: err.message }));
      }
    },

    async exportVersion({ rootState }, { env, versionId, query = {} }) {
      const projectId = rootState.project.projectInfo.id;
      const params = new URLSearchParams({
        projectid: projectId,
        ...query,
      });
      window.open(`${addPathPrefix(getPathPrefix(env))}/project/download/${versionId}?${params}`, '_blank');
    },
    async importVersion({ rootState, dispatch }, { env, version, desc, beforeFunc }) {
      try {
        const projectId = rootState.project.projectInfo.id;
        const url = `${getPathPrefix(env)}/project/import?projectid=${projectId}`;
        const result = await wujiFetch(url, {
          method: 'POST',
          body: JSON.stringify({
            version,
            desc,
            beforeFunc,
          }),
          timeout,
        });
        message.success($t('k_8ae82c14'));
        dispatch('fetchProjectVersionList', {
          projectId,
          env,
        });
        return result;
      } catch (err) {
        message.error($t('k_14533a37', { arg_0: err?.message ?? '' }));
        return null;
      }
    },
    async debugImportHook({ rootState }, { env, data, beforeFunc }) {
      try {
        const projectId = rootState.project.projectInfo.id;
        const url = `${getPathPrefix(env)}/project/hook/debug?projectid=${projectId}`;
        const result = await wujiFetch(url, {
          method: 'POST',
          body: JSON.stringify({
            data,
            beforeFunc,
          }),
          timeout,
        });
        return result;
      } catch (err) {
        message.error($t('k_6155e57b', { arg_0: err?.message ?? '' }));
        throw err;
      }
    },
    async fetchImportHooks({ rootState }) {
      try {
        const projectId = rootState.project.projectInfo.id;
        const url = `${getPathPrefix('dev')}/project/hook?projectid=${projectId}`;
        const result = await wujiFetch(url);
        return result;
      } catch (err) {
        message.error($t('k_ef623dfe', { arg_0: err?.message ?? '' }));
        throw err;
      }
    },
  },
  mutations: {
    updateVersionList(state, { env, versions }) {
      const { versionList } = state;
      versionList[env] = versions;
      Object.assign(state, { versionList: { ...versionList } });
    },
    updateVersionState(state, payload) {
      return Object.assign(state, payload);
    },
  },
};
